import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { select } from '@ptl/angular-redux-store';
import { BaseAgency, EntitlementsHelperService, LINE_OF_BUSINESS_ENUM, UserInfoStateStructure } from '@ptl/account-management';
import { BooleanInput, ContentStorageService } from '@ptl/global-components';
import { ProducerChangeAgencyModalComponent } from '../producer-change-agency/producer-change-agency-modal/producer-change-agency-modal.component';
import { Observable } from 'rxjs';
import { BrandingConfigurationService, brandValues } from '@ptl/brand';
import { NgRedux } from '@ptl/angular-redux-store';
import { Brand } from '@ptl/brand';
import { StateStructure } from '@ptl/state';
import { saveZipCounty } from '../../demographics/zip-county-modal/reducers/zipCounty.reducer';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
    selector: 'app-producer-compact-agency',
    templateUrl: './producer-compact-agency.component.html',
    styleUrls: ['./producer-compact-agency.component.scss'],
    standalone: false
})
export class ProducerCompactAgencyComponent implements OnInit {
  // AEM can only pass strings
  @Input() @BooleanInput() showRegionFilter: boolean;
  @Input() lob: LINE_OF_BUSINESS_ENUM;

  @select(['appStates', 'LoginInfo'])
  loginInfo$: Observable<any>;

  currentRegion: string;
  currentAgency: string;
  currentAgencyId: string;

  isReloading = false;

  constructor(
    private matDialog: MatDialog,
    private ngRedux: NgRedux<StateStructure>,
    private contentService: ContentStorageService,
    private brandService: BrandingConfigurationService,
    private entitlementService: EntitlementsHelperService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.loginInfo$.subscribe((loginInfo: UserInfoStateStructure) => {

      if (!loginInfo?.userInfo?.agencyInfo || this.isReloading) {
        return;
      }

      let selectedAgency = this.entitlementService.getSelectedAgency(this.lob);

      // Make sure we have a selected agency for this LOB
      if (!selectedAgency) {
        console.warn('No selected agency for line of business ' + this.lob);
        return;
      }

      // Initially sets the brand if there is not one set or it doesn't match our agency
      this.initAgencyBranding(selectedAgency);

      if (this.currentRegion && this.currentRegion !== selectedAgency.region) {
        // reload to capture new agency branding
        this.isReloading = true;
        let newBrand = brandValues.filter((brand) => brand.region.toUpperCase() === selectedAgency.region.toUpperCase());
        this.resetAgencyBranding(newBrand[0]);
      } else {
        this.currentRegion = selectedAgency.region;
        this.currentAgency = selectedAgency.agencyName;
        this.currentAgencyId = selectedAgency.agencyId;

      }
    });
  }

  changeAgency() {

    this.matDialog.open(ProducerChangeAgencyModalComponent, {
      id: 'producer-change-agency-modal',
      width: '400px',
      data: {
        lob: this.lob,
        showRegionFilter: this.showRegionFilter
      }
    });
  }

  // if brand isn't set or doesn't match, update the brand and refresh the page
  initAgencyBranding(selectedAgency: BaseAgency) {
    let curBrand = this.brandService.getCurrentBrand();
    // Fixing Agency for Sepa
    let agencyBrand = brandValues.filter((brand) => brand.region.toUpperCase() === selectedAgency.region.toUpperCase())[0];

    // END-SLASHES MATTER - they mean we're on a SUB-PAGE
    // Do not add non-slash-ending pages here...
    const newBusinessPath = "/individual/new-business/";
    const medicarePath = "/medicare/";
    const excludePathsFromCookieCheck = [
      newBusinessPath,
      medicarePath
    ];
    // ...add them here
    const unlessPathIsCookieCheck = {};
    unlessPathIsCookieCheck[newBusinessPath] = ["landing"]; // previously, we used "/indvidual/new-business" ( no end-slash ), but with reorg, we use "/individual/new-business/landing" which needs to be exempt

    let excludePath: boolean = false;
    const currentPage = window.location.pathname;
    for (let i = 0; i < excludePathsFromCookieCheck.length; i++) {

      // These pages DO NOT GET REFRESHED ( specifically called out, because others DO )
      const onPageWhichShouldNotRefresh = currentPage.includes(excludePathsFromCookieCheck[i]);

      // These pages act as a rebuttle to the above check - if we're on a page ( like /individual/new-business/* ) which does not get a refresh, but some children pages do ( like /individual/new-business/landing  )
      const deepPageRequiringRefresh = `${excludePathsFromCookieCheck[i]}${unlessPathIsCookieCheck[excludePathsFromCookieCheck[i]]}`;
      const unlessThatPageIsSpecificallyRequiredForRefreshing = !currentPage.includes(deepPageRequiringRefresh);

      if (onPageWhichShouldNotRefresh) {
        // Unless there is a specific page mapping - you will refresh to align brand
        if (unlessThatPageIsSpecificallyRequiredForRefreshing) {
          // Exclude means - do not refresh this page
          excludePath = true;
        }
        break;
      }
    }

    const DO_NOT_REFRESH = excludePath;

    /**
     * DEVON - 2024-09-27 - If we don't do this, then when changing the zip on the enter-shop-details page,
     * we enter an infinite loop where the page refreshes because the brand doesn't match because the brand cookie never actually gets updated
     * This is due to the order of operations: 
     * 1) producer-change-agency.component's zipCountyChange handler fires
     * 2) producer-change-agency.component's refreshPageOnBrandChange fires
     * 2a) cookie observed as unchanged
     * 3) refresh occurs 
     * [ timeout included before refresh to ensure state data updated in dexie ] - TODO we need to figure out how to appropriate synchronize dexie saves
     */
    if (!DO_NOT_REFRESH && (curBrand.cookieBrandCode.toLowerCase() !== agencyBrand.cookieBrandCode.toLowerCase())) {

      this.brandService.setBrandCookie(agencyBrand.cookieBrandCode);

      const alertDuration = 2500; // ms

      this.snackBar.open(`Updating site with current agency`, 'OK', {
        duration: alertDuration,
        panelClass: 'positive-panel-class'
      });

      setTimeout(() => {
        window.location.reload();
      }, alertDuration);
    }
  }

  resetAgencyBranding(newBrand: Brand) {

    this.brandService.setBrandCookie(newBrand.cookieBrandCode);

    // reset zip county info on brand change
    this.ngRedux.dispatch(saveZipCounty(null, null));

    // reload window to capture new brand
    const alertDuration = 1000; // ms

    this.snackBar.open(`Updating site with current agency`, 'OK', {
      duration: alertDuration,
      panelClass: 'positive-panel-class'
    });

    setTimeout(() => {
      window.location.reload();
    }, alertDuration);
  }
}
